import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link, useSearch } from '@tanstack/react-router'
import { ArrowLeft } from 'lucide-react'
import { z } from 'zod'
import { getCoachConsultation } from '@/api/coach'
import type { ConsultationStatus } from '@/api/consultations'
import { ConsultationDetailBody } from '@/components/consultation/consultation-detail-body'
import { Card, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { describeErrorMessage } from '@/lib/errors'

const POLL_INTERVAL_MS = 5_000

const searchSchema = z.object({
  // Карточка консультации не знает «своего» продавца до загрузки данных,
  // поэтому пробрасываем sellerId/period в search-параметрах при клике из
  // /coach/sellers/:id, чтобы кнопка «Назад» вернула на тот же фильтр.
  sellerId: z.string().optional(),
  period: z.enum(['week', 'month']).optional(),
  storeId: z.string().optional(),
})

export const Route = createFileRoute('/coach/consultations/$id')({
  validateSearch: searchSchema,
  component: CoachConsultationCard,
})

function CoachConsultationCard() {
  const { id } = Route.useParams()
  const search = useSearch({ from: '/coach/consultations/$id' })

  const query = useQuery({
    queryKey: ['coach', 'consultation', id],
    queryFn: () => getCoachConsultation(id),
    refetchInterval: (q) => {
      const data = q.state.data
      if (!data) return POLL_INTERVAL_MS
      return isTerminal(data.status) ? false : POLL_INTERVAL_MS
    },
  })

  // Семантика «назад»:
  //   • если в search есть sellerId — возврат на /coach/sellers/:id с тем же
  //     period, как требует T-058 acceptance «возврат сохраняет фильтры»;
  //   • иначе — на список магазинов (deep-link без контекста продавца).
  const backLink = search.sellerId
    ? {
        to: '/coach/sellers/$id' as const,
        params: { id: search.sellerId },
        search: { period: search.period, storeId: search.storeId },
      }
    : { to: '/coach/stores' as const }

  return (
    <main className="mx-auto flex w-full max-w-xl flex-col gap-4 px-4 py-6 pb-12">
      <Link
        {...backLink}
        className="-ml-3 inline-flex w-fit items-center gap-2 rounded-md px-3 py-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden="true" />
        {search.sellerId ? 'К продавцу' : 'К списку магазинов'}
      </Link>

      {query.isLoading ? (
        <Card>
          <CardHeader>
            <CardTitle>Загружаем карточку</CardTitle>
          </CardHeader>
        </Card>
      ) : query.isError ? (
        <Card>
          <CardHeader>
            <CardTitle>Не удалось загрузить</CardTitle>
            <CardDescription>
              {describeErrorMessage(query.error, 'Попробуйте обновить страницу.')}
            </CardDescription>
          </CardHeader>
        </Card>
      ) : query.data ? (
        <ConsultationDetailBody card={query.data} onRefresh={() => void query.refetch()} />
      ) : null}
    </main>
  )
}

function isTerminal(status: ConsultationStatus): boolean {
  return status === 'done' || status === 'failed'
}
